import React from 'react';
import Header from '../components/landing/Header';
import Footer from '../components/landing/Footer';
import { techHighlights, analyticsMetrics } from '../data/mock';
import { Server, Shield, Zap, Cpu, BarChart3, TrendingUp, CheckCircle, History, ArrowUp, ArrowDown, Puzzle, RefreshCw, ArrowRight } from 'lucide-react';

const techIcons = [Server, Shield, Zap, Cpu];

const integrations = [
  { name: 'Slack', category: 'Communication' },
  { name: 'Microsoft Teams', category: 'Communication' },
  { name: 'Google Calendar', category: 'Scheduling' },
  { name: 'Outlook', category: 'Email' },
  { name: 'LinkedIn', category: 'Sourcing' },
  { name: 'Greenhouse', category: 'ATS' },
  { name: 'Workday', category: 'HRIS' },
  { name: 'Zoom', category: 'Video' }
];

const PlatformPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero */}
      <section className="pt-32 pb-20 bg-white">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-3xl">
            <span className="text-neutral-400 text-sm font-medium uppercase tracking-widest mb-4 block">Sandgen Connect</span>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl text-black leading-[0.95] mb-6 tracking-tight">
              The backbone
              <br />
              <span className="text-neutral-400">you deserve.</span>
            </h1>
            <p className="text-xl text-neutral-500 leading-relaxed">
              While others promise uptime, we deliver obsession. Enterprise infrastructure 
              so reliable, you&apos;ll forget it exists.
            </p>
          </div>
        </div>
      </section>

      {/* Tech Highlights */}
      <section className="section bg-black relative">
        <div className="absolute inset-0 grid-bg-dark" />
        <div className="container mx-auto px-6 lg:px-12 relative z-10">
          <h2 className="font-display text-3xl md:text-4xl text-white mb-12">Engineering. Perfected.</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {techHighlights.map((item, index) => {
              const Icon = techIcons[index];
              return (
                <div key={index} className="bg-neutral-900 border border-neutral-800 p-10 text-center group hover:border-neutral-700 transition-all duration-300">
                  <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-6">
                    <Icon className="w-8 h-8 text-black" />
                  </div>
                  <h3 className="text-white text-lg font-semibold mb-3">{item.title}</h3>
                  <p className="text-neutral-500 text-sm">{item.description}</p>
                </div>
              );
            })}
          </div>

          <div className="mt-16 grid md:grid-cols-2 gap-6">
            <div className="bg-neutral-900 border border-neutral-800 p-10">
              <h3 className="text-white text-xl font-semibold mb-4">Microservices. Obviously.</h3>
              <p className="text-neutral-400 mb-6">
                Monoliths are so 2015. Our architecture scales independently, deploys continuously, and never breaks a sweat.
              </p>
              <div className="flex flex-wrap gap-2">
                {['Sandgen Core', 'Sandgen Mind', 'Sandgen Stage', 'Sandgen Prove'].map((item) => (
                  <span key={item} className="px-4 py-2 bg-neutral-800 text-neutral-300 text-sm rounded-full">{item}</span>
                ))}
              </div>
            </div>
            <div className="bg-neutral-900 border border-neutral-800 p-10">
              <h3 className="text-white text-xl font-semibold mb-4">Fort Knox. But Prettier.</h3>
              <p className="text-neutral-400 mb-6">
                Your data isn&apos;t just protected—it&apos;s pampered. End-to-end encryption with audit trails so thorough, even we can&apos;t peek.
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="px-4 py-2 bg-white text-black text-sm font-medium rounded-full">SOC 2 Type II</span>
                {['GDPR Certified', '256-bit AES', 'Enterprise SSO'].map((item) => (
                  <span key={item} className="px-4 py-2 bg-neutral-800 text-neutral-300 text-sm rounded-full">{item}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Analytics */}
      <section className="section bg-neutral-50">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-neutral-400 text-sm font-medium uppercase tracking-widest mb-4 block">Sandgen Pulse</span>
            <h2 className="font-display text-4xl md:text-5xl text-black leading-[1] mb-6 tracking-tight">
              Know everything. <span className="text-neutral-400">Miss nothing.</span>
            </h2>
            <p className="text-lg text-neutral-500">
              Dashboards so intuitive, they practically read themselves. Reports so comprehensive, they&apos;ll make your CFO weep.
            </p>
          </div>

          <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden shadow-xl">
            <div className="bg-neutral-50 px-6 py-4 border-b border-neutral-200 flex items-center justify-between">
              <h4 className="text-black font-medium">Hiring Dashboard</h4>
              <span className="px-3 py-1.5 bg-black text-white text-sm rounded-full font-medium">Last 30 Days</span>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 border-b border-neutral-200">
              {[
                { label: 'Applications', value: '1,284', change: '+12%', icon: BarChart3, up: true },
                { label: 'Stage Conversion', value: '68%', change: '+5%', icon: TrendingUp, up: true },
                { label: 'Offers Accepted', value: '89%', change: '+3%', icon: CheckCircle, up: true },
                { label: 'Avg. Time to Hire', value: '18d', change: '-22%', icon: History, up: false }
              ].map((item, index) => (
                <div key={index} className="p-6 border-r border-neutral-200 last:border-r-0">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-neutral-500 text-sm">{item.label}</span>
                    <item.icon className="w-4 h-4 text-neutral-400" />
                  </div>
                  <div className="text-3xl font-semibold text-black mb-1">{item.value}</div>
                  <div className="flex items-center gap-1 text-neutral-600 text-sm">
                    {item.up ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
                    <span>{item.change}</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="grid lg:grid-cols-2">
              <div className="p-8 border-r border-neutral-200">
                <h5 className="text-black font-medium mb-6">Pipeline Distribution</h5>
                <div className="space-y-5">
                  {[
                    { stage: 'Application', value: 100, count: 1284 },
                    { stage: 'Assessment', value: 72, count: 925 },
                    { stage: 'Interview', value: 45, count: 578 },
                    { stage: 'Offer', value: 12, count: 154 }
                  ].map((item) => (
                    <div key={item.stage}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-neutral-700 text-sm">{item.stage}</span>
                        <span className="text-neutral-500 text-sm">{item.count}</span>
                      </div>
                      <div className="w-full h-2 bg-neutral-100 rounded-full overflow-hidden">
                        <div className="h-full bg-black rounded-full" style={{ width: `${item.value}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="p-8">
                <h5 className="text-black font-medium mb-6">Recent Activity</h5>
                <div className="space-y-5">
                  {[
                    { action: 'Offer sent to Sarah Chen', time: '2 hours ago' },
                    { action: 'Interview completed - John Doe', time: '4 hours ago' },
                    { action: '32 new applications received', time: '6 hours ago' },
                    { action: 'Assessment batch completed', time: '1 day ago' }
                  ].map((item, index) => (
                    <div key={index} className="flex items-center gap-4">
                      <div className="w-2 h-2 bg-black rounded-full flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-neutral-700 text-sm">{item.action}</p>
                        <p className="text-neutral-400 text-xs">{item.time}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Integrations */}
      <section className="section bg-white">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-neutral-400 text-sm font-medium uppercase tracking-widest mb-4 block">Sandgen Sync</span>
              <h2 className="font-display text-4xl md:text-5xl text-black leading-[1] mb-6 tracking-tight">
                Plays well <span className="text-neutral-400">with others.</span>
              </h2>
              <p className="text-lg text-neutral-500 mb-10">
                Your tech stack, elevated. Sandgen doesn&apos;t just integrate—it orchestrates.
              </p>
              <div className="space-y-8">
                {[
                  { icon: Puzzle, title: 'Native Connectors', desc: 'One-click integrations. Zero configuration headaches.' },
                  { icon: RefreshCw, title: 'Real-Time Sync', desc: 'Data flows like water. Always fresh. Never stale.' },
                  { icon: Zap, title: 'Developer API', desc: 'RESTful. Documented. Actually enjoyable to use.' }
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-5">
                    <div className="w-12 h-12 bg-neutral-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-6 h-6 text-neutral-600" />
                    </div>
                    <div>
                      <h4 className="text-black font-semibold mb-2">{item.title}</h4>
                      <p className="text-neutral-500">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {integrations.map((integration, index) => (
                <div key={index} className="bg-neutral-50 border border-neutral-100 p-8 group hover:bg-white hover:border-neutral-200 hover:shadow-lg transition-all duration-300">
                  <div className="w-12 h-12 bg-white border border-neutral-200 rounded-xl flex items-center justify-center mb-5 group-hover:bg-black group-hover:border-black group-hover:text-white transition-all duration-300">
                    <span className="font-semibold text-lg">{integration.name[0]}</span>
                  </div>
                  <h4 className="text-black font-medium mb-1">{integration.name}</h4>
                  <p className="text-neutral-500 text-sm">{integration.category}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PlatformPage;
